import supabase from '../lib/supabaseClient';
import bcrypt from 'bcryptjs';

/**
 * Mover Authentication Service
 * Handles authentication and application status checks for movers and team members
 */

/**
 * Sign in a mover or team member and check their status
 * @param {string} email - Mover's or team member's email
 * @param {string} password - Password
 * @returns {Object} - { success, status, message, reason, user, moverData, isTeamMember }
 */
export const signInMover = async (email, password) => {
  try {
    // Step 0: Check if this is a team member login
    const { data: teamMember, error: teamError } = await supabase
      .from('team_members')
      .select('*, movers_data!inner(*)')
      .eq('email', email)
      .eq('is_active', true)
      .single();

    if (teamMember && !teamError) {
      // This is a team member - verify password with bcrypt
      const passwordMatch = await bcrypt.compare(password, teamMember.password_hash);

      if (!passwordMatch) {
        return {
          success: false,
          status: 'error',
          message: 'Invalid email or password'
        };
      }

      // Try to authenticate the team member through Supabase Auth for secure access
      const { data: teamAuthData, error: teamAuthError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      // Check if team member account is banned
      if (teamAuthError && (teamAuthError.message.includes('banned') || teamAuthError.message.includes('User banned'))) {
        return {
          success: false,
          status: 'suspended',
          message: 'Your account has been suspended.',
          details: 'Please contact the administration team to resolve this issue.'
        };
      }

      // If no Supabase Auth account exists, create one automatically
      if (teamAuthError) {
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              first_name: teamMember.first_name,
              last_name: teamMember.last_name,
              role: 'team_member'
            }
          }
        });

        if (signUpError) {
          return {
            success: false,
            status: 'error',
            message: 'Failed to set up secure authentication. Please contact your administrator.'
          };
        }

        // Now sign in with the newly created account
        const { data: newAuthData, error: newAuthError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (newAuthError) {
          // Check if the newly created account was immediately banned
          if (newAuthError.message.includes('banned') || newAuthError.message.includes('User banned')) {
            return {
              success: false,
              status: 'suspended',
              message: 'Your account has been suspended.',
              details: 'Please contact the administration team to resolve this issue.'
            };
          }

          return {
            success: false,
            status: 'error',
            message: 'Authentication setup incomplete. Please try logging in again.'
          };
        }
      }

      // Team member authenticated successfully
      // Store team member info in session storage for access control
      sessionStorage.setItem('isTeamMember', 'true');
      sessionStorage.setItem('teamMemberId', teamMember.id);
      sessionStorage.setItem('teamMemberEmail', teamMember.email);
      sessionStorage.setItem('moverEmail', teamMember.movers_data.email);

      return {
        success: true,
        status: 'approved',
        message: 'Login successful!',
        isTeamMember: true,
        teamMemberData: teamMember,
        moverData: teamMember.movers_data
      };
    }

    // Step 1: Authenticate with Supabase (regular mover login)
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (authError) {
      // Check if error is due to user being banned/suspended
      if (authError.message.includes('banned') || authError.message.includes('User banned')) {
        return {
          success: false,
          status: 'suspended',
          message: 'Your account has been suspended.',
          details: 'Please contact the administration team to resolve this issue.'
        };
      }

      return {
        success: false,
        status: 'error',
        message: authError.message
      };
    }

    // Step 2: Check if mover exists in movers_data (approved movers)
    const { data: moverData, error: moverError } = await supabase
      .from('movers_data')
      .select('*')
      .eq('email', email)
      .single();

    if (moverData) {
      // Mover is approved and in movers_data table
      // Clear team member flags
      sessionStorage.removeItem('isTeamMember');
      sessionStorage.removeItem('teamMemberId');
      sessionStorage.removeItem('teamMemberEmail');
      sessionStorage.removeItem('moverEmail');

      return {
        success: true,
        status: 'approved',
        message: 'Login successful!',
        user: authData.user,
        moverData: moverData,
        isTeamMember: false
      };
    }

    // Step 3: Check application status in movers_application
    const { data: application, error: appError } = await supabase
      .from('movers_application')
      .select('*')
      .eq('email', email)
      .single();

    if (appError || !application) {
      // No application found in either table
      await supabase.auth.signOut();
      return {
        success: false,
        status: 'error',
        message: 'No mover application found for this account.'
      };
    }

    // Step 4: Handle different application statuses
    switch (application.status) {
      case 'Pending':
        // Sign out user - they need to wait for approval
        await supabase.auth.signOut();
        return {
          success: false,
          status: 'pending',
          message: 'Your application is currently under review.',
          details: 'Our team is reviewing your application. You will receive an email once your account has been approved. This process typically takes 1-2 business days.'
        };

      case 'Rejected':
        // Sign out user - application was rejected
        await supabase.auth.signOut();
        return {
          success: false,
          status: 'rejected',
          message: 'Your application has been rejected.',
          reason: application.rejection_reason || 'No specific reason provided.'
        };

      default:
        await supabase.auth.signOut();
        return {
          success: false,
          status: 'error',
          message: 'Unknown application status. Please contact support.'
        };
    }
  } catch (error) {
    return {
      success: false,
      status: 'error',
      message: 'An unexpected error occurred. Please try again.'
    };
  }
};

/**
 * Check if the current user is an approved mover or team member
 * @returns {Object} - { isApproved, moverData, isTeamMember }
 */
export const checkMoverStatus = async () => {
  try {
    // First check if this is a team member session
    const isTeam = sessionStorage.getItem('isTeamMember') === 'true';
    const moverEmail = sessionStorage.getItem('moverEmail');

    if (isTeam && moverEmail) {
      // Get the mover data for this team member
      const { data: moverData, error: moverError } = await supabase
        .from('movers_data')
        .select('*')
        .eq('email', moverEmail)
        .single();

      if (moverData && !moverError) {
        return {
          isApproved: true,
          moverData: moverData,
          isTeamMember: true
        };
      }
    }

    // Regular mover authentication
    const { data: { user }, error: authError } = await supabase.auth.getUser();

    if (authError || !user) {
      return { isApproved: false, moverData: null, isTeamMember: false };
    }

    // Phone-auth users (normal users) don't have email - skip mover checks entirely
    if (!user.email) {
      return { isApproved: false, moverData: null, isTeamMember: false };
    }

    // First check if mover exists in movers_data (approved movers)
    const { data: moverData, error: moverError } = await supabase
      .from('movers_data')
      .select('*')
      .eq('email', user.email)
      .single();

    if (moverData) {
      return {
        isApproved: true,
        moverData: moverData,
        isTeamMember: false
      };
    }

    // If not in movers_data, check application status
    const { data: application, error: appError } = await supabase
      .from('movers_application')
      .select('*')
      .eq('email', user.email)
      .single();

    if (appError || !application || application.status !== 'Approved') {
      return { isApproved: false, moverData: null, isTeamMember: false };
    }

    // Application is approved but not yet in movers_data
    return { isApproved: false, moverData: null, isTeamMember: false };
  } catch (error) {
    return { isApproved: false, moverData: null, isTeamMember: false };
  }
};

/**
 * Sign out the current mover or team member
 * @returns {Object} - { success, message }
 */
export const signOutMover = async () => {
  try {
    // Clear team member session storage
    sessionStorage.removeItem('isTeamMember');
    sessionStorage.removeItem('teamMemberId');
    sessionStorage.removeItem('teamMemberEmail');
    sessionStorage.removeItem('moverEmail');

    const { error } = await supabase.auth.signOut();

    if (error) {
      return {
        success: false,
        message: error.message
      };
    }

    return {
      success: true,
      message: 'Signed out successfully'
    };
  } catch (error) {
    return {
      success: false,
      message: 'An error occurred during sign out'
    };
  }
};

const moverAuthService = {
  signInMover,
  checkMoverStatus,
  signOutMover
};

export default moverAuthService;
